import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { fetchFeatured, seedData, seedReviews, mockCheckout, fetchSocialProof, createCheckoutSession } from '@/lib/api';
import { trackPurchase, trackAbandonedCart, trackPromoCodeCopy, trackBuyNowClick, trackProductClick, trackRedirectToMainSite } from '@/lib/tracking';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ShoppingBag, User, Settings, LogOut, Copy, Check, Tag, Clock, Truck, Shield, BookOpen, ExternalLink, ArrowRight, Star, TrendingUp, Quote, CreditCard, Mail, MapPin } from 'lucide-react';
import { Label } from '@/components/ui/label';
import MobileInstallBanner from '@/components/MobileInstallBanner';
import { requestPushPermission, checkAbandonedCarts, checkScheduledPush } from '@/lib/push';

function useCountdown(targetDate) {
  const calc = useCallback(() => {
    if (!targetDate) return null;
    const diff = new Date(targetDate) - new Date();
    if (diff <= 0) return null;
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff / 3600000) % 24),
      minutes: Math.floor((diff / 60000) % 60),
      seconds: Math.floor((diff / 1000) % 60)
    };
  }, [targetDate]);
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [calc]);
  return time;
}

export default function Storefront() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutProduct, setCheckoutProduct] = useState(null);
  const [promoInput, setPromoInput] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [socialProof, setSocialProof] = useState(null);
  const [animatedSales, setAnimatedSales] = useState(0);
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [billingForm, setBillingForm] = useState({ name: '', email: '', tax_code: '', address: '', city: '', zip: '', country: 'IT' });
  const [shippingForm, setShippingForm] = useState({ name: '', address: '', city: '', zip: '', country: 'IT' });
  const [deliveryEmail, setDeliveryEmail] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [billingType, setBillingType] = useState('ricevuta');
  const [invoiceForm, setInvoiceForm] = useState({ company_name: '', vat_number: '', sdi_code: '' });

  useEffect(() => {
    (async () => {
      try { await seedData(); } catch {}
      try { await seedReviews(); } catch {}
      try {
        const res = await fetchFeatured();
        setProducts(res.data);
        const active = res.data.find(p => p.is_currently_active);
        if (active) {
          try {
            const sp = await fetchSocialProof(active.campaign_id);
            setSocialProof(sp.data);
          } catch {}
        }
      } catch { toast.error('Errore nel caricamento dei prodotti'); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    const h = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', h);
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);

  const activeProduct = products.find(p => p.is_currently_active);
  const inactiveProducts = products.filter(p => !p.is_currently_active);
  const countdown = useCountdown(activeProduct?.active_until);

  // Animated sales counter
  useEffect(() => {
    if (!socialProof?.weekly_sales) return;
    const target = socialProof.weekly_sales;
    let current = 0;
    const step = Math.max(1, Math.floor(target / 40));
    const timer = setInterval(() => {
      current = Math.min(current + step, target);
      setAnimatedSales(current);
      if (current >= target) clearInterval(timer);
    }, 30);
    return () => clearInterval(timer);
  }, [socialProof?.weekly_sales]);

  // Request push permission + check scheduled/abandoned
  useEffect(() => {
    const timer = setTimeout(() => {
      requestPushPermission();
      checkAbandonedCarts();
      checkScheduledPush();
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleBuyNow = (product) => {
    trackBuyNowClick(product.campaign_id, product.title, product.price);
    setCheckoutProduct(product);
    setPromoInput(product.promo_code || '');
    setCheckoutStep(1);
    setBillingForm(prev => ({ ...prev, email: user?.email || '', name: user?.name || '' }));
    setDeliveryEmail(user?.email || '');
    setCheckoutOpen(true);
  };

  const handleCheckout = async () => {
    if (!checkoutProduct) return;
    if (!billingForm.name || !billingForm.email) {
      toast.error('Compila nome e email di fatturazione');
      return;
    }
    const tc = (billingForm.tax_code || '').replace(/\s/g, '').toUpperCase();
    if (!tc) { toast.error('Codice Fiscale obbligatorio'); return; }
    if (tc.length !== 16 && tc.length !== 11) {
      toast.error('Codice Fiscale non valido (16 caratteri per persona fisica, 11 cifre per P.IVA)');
      return;
    }
    const isDigital = checkoutProduct.is_digital;
    if (!isDigital && (!shippingForm.address || !shippingForm.city)) {
      toast.error('Compila l\'indirizzo di spedizione');
      return;
    }
    if (isDigital && !deliveryEmail) {
      toast.error('Inserisci l\'email di consegna');
      return;
    }
    setCheckoutLoading(true);
    // Auto-determine document type: if any invoice field filled -> fattura, else ricevuta
    const wantsInvoice = !!(invoiceForm.vat_number || invoiceForm.sdi_code || invoiceForm.company_name);
    try {
      const res = await createCheckoutSession({
        campaign_id: checkoutProduct.campaign_id,
        promo_code: promoInput,
        payment_method: paymentMethod,
        origin_url: window.location.origin,
        billing_name: billingForm.name, billing_email: billingForm.email,
        billing_address: billingForm.address, billing_city: billingForm.city,
        billing_zip: billingForm.zip, billing_country: billingForm.country,
        tax_code: tc,
        shipping_name: shippingForm.name || billingForm.name,
        shipping_address: shippingForm.address, shipping_city: shippingForm.city,
        shipping_zip: shippingForm.zip, shipping_country: shippingForm.country || billingForm.country,
        delivery_email: deliveryEmail,
        billing_type: wantsInvoice ? 'fattura' : 'ricevuta',
        company_name: invoiceForm.company_name,
        vat_number: invoiceForm.vat_number,
        sdi_code: invoiceForm.sdi_code
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante il pagamento');
    } finally { setCheckoutLoading(false); }
  };

  const handleCheckoutClose = (open) => {
    if (!open && checkoutProduct) trackAbandonedCart(checkoutProduct.campaign_id, checkoutProduct.title);
    setCheckoutOpen(open);
  };

  const copyPromo = (code, campaignId) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    trackPromoCodeCopy(campaignId, code);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Codice copiato negli appunti!');
  };

  const computeDiscountedPrice = (p) => {
    if (!p) return null;
    const dv = p.promo_discount_value || 0;
    if (dv <= 0) return p.price.toFixed(2);
    const dt = p.promo_discount_type || 'percentage';
    const discountAmount = dt === 'fixed' ? Math.min(dv, p.price) : (p.price * dv) / 100;
    return Math.max(0, p.price - discountAmount).toFixed(2);
  };
  const discountedPrice = computeDiscountedPrice(activeProduct);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Mobile Install Banner */}
      <MobileInstallBanner />
      {/* Header - Bookstore navigation */}
      <header data-testid="storefront-header" className="sticky top-0 z-50 w-full border-b" style={{ backgroundColor: 'var(--bg-dark)', borderColor: 'rgba(96,165,250,0.15)' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-3" style={{ textDecoration: 'none' }}>
            <BookOpen className="w-5 h-5" style={{ color: 'var(--accent-light)' }} />
            <span className="text-lg tracking-tight" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-on-dark)', fontWeight: 600 }}>
              Quintessenza
            </span>
            <span className="hidden sm:inline text-xs tracking-widest uppercase" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)', fontWeight: 300 }}>
              Libreria
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/magazine" data-testid="nav-magazine-link"
              className="text-xs tracking-wider uppercase px-2 py-1 transition-colors hover:opacity-80"
              style={{ color: 'var(--accent-light)', textDecoration: 'none', fontFamily: 'var(--font-body)' }}>
              Rivista
            </Link>
            {installPrompt && (
              <button onClick={() => { installPrompt.prompt(); setInstallPrompt(null); }} data-testid="pwa-install-btn"
                className="text-xs px-3 py-1.5 rounded-sm border transition-colors"
                style={{ borderColor: 'rgba(96,165,250,0.3)', color: 'var(--accent-light)' }}>
                Installa App
              </button>
            )}
            {user ? (
              <div className="flex items-center gap-2">
                <span className="hidden sm:inline text-xs" style={{ color: 'var(--text-muted)' }}>
                  {user.name}
                </span>
                {user.role === 'admin' && (
                  <button onClick={() => navigate('/admin')} data-testid="admin-link"
                    className="text-xs px-2 py-1 rounded-sm transition-colors" style={{ color: 'var(--accent-light)' }}>
                    <Settings className="w-3.5 h-3.5 inline mr-1" />Admin
                  </button>
                )}
                <button onClick={logout} data-testid="logout-btn" className="p-1.5 rounded-sm transition-colors" style={{ color: 'var(--text-muted)' }}>
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button onClick={() => navigate('/auth?mode=login')} data-testid="login-btn"
                  className="text-xs px-3 py-1.5 rounded-sm transition-colors"
                  style={{ color: 'var(--accent-light)' }}>
                  Accedi
                </button>
                <button onClick={() => navigate('/auth?mode=register')} data-testid="register-btn"
                  className="text-xs px-4 py-1.5 rounded-sm transition-colors font-medium"
                  style={{ backgroundColor: 'rgba(59,130,246,0.9)', color: '#fff' }}>
                  <User className="w-3.5 h-3.5 inline mr-1" />Registrati
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* HERO: Active Product Spotlight */}
      {!loading && activeProduct && (
        <section data-testid="hero-section" className="hero-dark">
          <div className="max-w-7xl mx-auto px-5 md:px-10 py-12 md:py-20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              {/* Book Image */}
              <div className="flex justify-center animate-scale-in">
                <div className="book-spotlight relative">
                  <img src={activeProduct.image_url} alt={activeProduct.title}
                    className="w-64 md:w-80 h-auto rounded-sm"
                    style={{ boxShadow: '8px 8px 30px rgba(0,0,0,0.4), 0 0 20px rgba(37,99,235,0.15)' }}
                    data-testid="hero-book-image" />
                  {activeProduct.badge && (
                    <Badge data-testid={`product-badge-${activeProduct.campaign_id}`}
                      className="absolute -top-3 -right-3 px-3 py-1.5 text-xs rounded-sm"
                      style={{ backgroundColor: 'var(--accent)', color: '#fff', fontFamily: 'var(--font-body)', fontWeight: 500 }}>
                      {activeProduct.badge}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Product Info */}
              <div className="text-center md:text-left">
                <p className="text-xs tracking-[0.3em] uppercase mb-4 animate-fade-in-up stagger-1"
                  style={{ fontFamily: 'var(--font-body)', color: 'var(--accent-light)', fontWeight: 400 }}>
                  Offerta della Settimana
                </p>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-3 animate-fade-in-up stagger-2"
                  style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-on-dark)', fontWeight: 600 }}>
                  {activeProduct.title}
                </h1>
                <p className="text-sm mb-4 animate-fade-in-up stagger-2"
                  style={{ fontFamily: 'var(--font-body)', color: 'var(--accent-light)', fontWeight: 300 }}>
                  {activeProduct.subtitle}
                </p>
                <p className="text-sm mb-6 max-w-md animate-fade-in-up stagger-3"
                  style={{ fontFamily: 'var(--font-body)', color: 'rgba(226,232,240,0.6)', fontWeight: 300 }}>
                  {activeProduct.description}
                </p>

                {/* Price block */}
                <div className="flex items-baseline gap-3 mb-5 justify-center md:justify-start animate-fade-in-up stagger-3">
                  <span className="text-3xl font-medium" style={{ fontFamily: 'var(--font-heading)', color: 'var(--accent-light)' }}>
                    {discountedPrice} EUR
                  </span>
                  {activeProduct.compare_at_price && (
                    <span className="text-base line-through" style={{ color: 'var(--text-muted)' }}>
                      {activeProduct.compare_at_price.toFixed(2)} EUR
                    </span>
                  )}
                  {activeProduct.promo_discount_value > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-sm"
                      style={{ backgroundColor: 'rgba(37,99,235,0.2)', color: 'var(--accent-light)', fontFamily: 'var(--font-mono)' }}>
                      {activeProduct.promo_discount_type === 'fixed'
                        ? `-${activeProduct.promo_discount_value} EUR`
                        : `-${activeProduct.promo_discount_value}%`}
                    </span>
                  )}
                </div>

                {/* Promo Code Widget */}
                {activeProduct.promo_code && (
                  <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm mb-6 promo-pulse animate-fade-in-up stagger-4"
                    style={{ backgroundColor: 'rgba(37,99,235,0.12)', border: '1px solid rgba(96,165,250,0.3)' }}
                    data-testid="hero-promo-banner">
                    <Tag className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--accent-light)' }} />
                    <span className="text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-light)' }}>
                      {activeProduct.promo_code}
                    </span>
                    <button onClick={() => copyPromo(activeProduct.promo_code, activeProduct.campaign_id)}
                      data-testid="hero-copy-promo-btn"
                      className="ml-1 p-1 rounded hover:bg-white/10 transition-colors">
                      {copied ? <Check className="w-3.5 h-3.5" style={{ color: '#7dbd5a' }} /> : <Copy className="w-3.5 h-3.5" style={{ color: 'var(--accent-light)' }} />}
                    </button>
                  </div>
                )}

                {/* Countdown Timer */}
                {countdown && (
                  <div className="flex items-center gap-1.5 mb-7 justify-center md:justify-start animate-fade-in-up stagger-4" data-testid="countdown-timer">
                    <Clock className="w-3.5 h-3.5 mr-1" style={{ color: 'var(--text-muted)' }} />
                    <span className="text-xs" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>Termina tra</span>
                    {[
                      { val: countdown.days, label: 'g' },
                      { val: countdown.hours, label: 'h' },
                      { val: countdown.minutes, label: 'm' },
                      { val: countdown.seconds, label: 's' },
                    ].map(({ val, label }) => (
                      <span key={label} className="countdown-unit inline-flex items-baseline px-1.5 py-0.5 rounded-sm"
                        style={{ backgroundColor: 'rgba(96,165,250,0.1)', fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-on-dark)' }}>
                        {String(val).padStart(2, '0')}<span className="text-xs ml-0.5" style={{ color: 'var(--text-muted)' }}>{label}</span>
                      </span>
                    ))}
                  </div>
                )}

                {/* CTA Button */}
                <div className="flex items-center gap-4 flex-wrap">
                  <Button onClick={() => handleBuyNow(activeProduct)} data-testid={`buy-now-btn-${activeProduct.campaign_id}`}
                    className="cta-glow rounded-sm px-10 py-6 text-base font-medium tracking-wide"
                    style={{ backgroundColor: '#3B82F6', color: '#fff', fontFamily: 'var(--font-body)', border: 'none', textShadow: '0 0 10px rgba(255,255,255,0.3)' }}>
                    <ShoppingBag className="w-5 h-5 mr-2" /> Acquista Ora
                  </Button>
                  <button onClick={() => navigate(`/product/${activeProduct.campaign_id}`)} data-testid="hero-details-link"
                    className="inline-flex items-center gap-1.5 text-sm underline underline-offset-4 transition-opacity hover:opacity-70"
                    style={{ color: 'var(--accent-light)' }}>
                    Scopri i dettagli <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Loading state */}
      {loading && (
        <section className="hero-dark py-32">
          <div className="flex justify-center">
            <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent-light)', borderTopColor: 'transparent' }} />
          </div>
        </section>
      )}

      {/* No active product fallback */}
      {!loading && !activeProduct && products.length > 0 && (
        <section className="hero-dark py-20">
          <div className="max-w-7xl mx-auto px-5 md:px-10 text-center">
            <h1 className="text-3xl sm:text-4xl tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-on-dark)', fontWeight: 600 }}>
              Capsule Mensile
            </h1>
            <p className="text-sm max-w-lg mx-auto" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)', fontWeight: 300 }}>
              Nessuna offerta attiva al momento. Esplora la nostra selezione editoriale del mese.
            </p>
          </div>
        </section>
      )}

      {/* SOCIAL PROOF SECTION */}
      {!loading && activeProduct && socialProof && (
        <section data-testid="social-proof-section" className="py-12 md:py-16" style={{ backgroundColor: 'var(--bg-primary)' }}>
          <div className="max-w-7xl mx-auto px-5 md:px-10">
            {/* Sales Counter + Rating Bar */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 mb-10">
              {/* Sales Counter */}
              <div className="flex items-center gap-3" data-testid="sales-counter">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(37,99,235,0.1)' }}>
                  <TrendingUp className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                </div>
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-medium" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}>
                      {animatedSales}
                    </span>
                    <span className="text-sm" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', fontWeight: 300 }}>
                      copie vendute
                    </span>
                  </div>
                  <p className="text-xs" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)' }}>questa settimana</p>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:block w-px h-10" style={{ backgroundColor: 'var(--border-light)' }} />

              {/* Rating */}
              {socialProof.avg_rating > 0 && (
                <div className="flex items-center gap-3" data-testid="avg-rating">
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-4 h-4" fill={i <= Math.round(socialProof.avg_rating) ? '#F59E0B' : 'none'}
                        stroke={i <= Math.round(socialProof.avg_rating) ? '#F59E0B' : 'var(--border-light)'} />
                    ))}
                  </div>
                  <div>
                    <span className="text-lg font-medium" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}>
                      {socialProof.avg_rating}
                    </span>
                    <span className="text-xs ml-1" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)' }}>
                      ({socialProof.total_reviews} recensioni)
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Reviews */}
            {socialProof.reviews && socialProof.reviews.length > 0 && (
              <div>
                <p className="text-xs tracking-[0.25em] uppercase mb-5 text-center" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)' }}>
                  Cosa dicono i professionisti
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {socialProof.reviews.slice(0, 3).map((rev, i) => (
                    <div key={rev.review_id || i} data-testid={`review-card-${i}`}
                      className={`p-5 rounded-sm border animate-fade-in-up stagger-${i + 1}`}
                      style={{ backgroundColor: 'var(--bg-white)', borderColor: 'var(--border-light)' }}>
                      <div className="flex items-center gap-2 mb-3">
                        <Quote className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-light)', opacity: 0.5 }} />
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className="w-3 h-3" fill={s <= rev.rating ? '#F59E0B' : 'none'}
                              stroke={s <= rev.rating ? '#F59E0B' : 'var(--border-light)'} />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm mb-3 leading-relaxed" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', fontWeight: 300 }}>
                        "{rev.text}"
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium"
                          style={{ backgroundColor: 'var(--bg-warm)', color: 'var(--accent)', fontFamily: 'var(--font-body)' }}>
                          {rev.user_name?.charAt(0) || 'U'}
                        </div>
                        <span className="text-xs font-medium" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
                          {rev.user_name}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* COLLECTION SHELF: Inactive products */}
      {!loading && inactiveProducts.length > 0 && (
        <section data-testid="products-section" className="py-16 md:py-24" style={{ backgroundColor: 'var(--bg-warm)' }}>
          <div className="max-w-7xl mx-auto px-5 md:px-10">
            <div className="flex items-center justify-between mb-10">
              <div>
                <p className="text-xs tracking-[0.25em] uppercase mb-2" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)' }}>
                  La Collezione
                </p>
                <h2 className="text-xl md:text-2xl tracking-tight" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)', fontWeight: 600 }}>
                  Selezione del Mese
                </h2>
              </div>
              <p className="text-xs hidden sm:block" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)' }}>
                Disponibili sul sito principale
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {inactiveProducts.map((p, i) => (
                <div key={p.campaign_id} data-testid={`product-card-${p.campaign_id}`}
                  onClick={() => { trackProductClick(p.campaign_id, p.title); navigate(`/product/${p.campaign_id}`); }}
                  className={`shelf-card bg-white border rounded-sm overflow-hidden cursor-pointer animate-fade-in-up stagger-${i + 2}`}
                  style={{ borderColor: 'var(--border-light)' }}>
                  <div className="aspect-[4/5] w-full relative overflow-hidden" style={{ backgroundColor: '#eae5dc' }}>
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                    {p.badge && (
                      <Badge className="absolute top-3 left-3 text-xs px-2 py-0.5 rounded-sm"
                        style={{ backgroundColor: 'var(--espresso)', color: '#fff', fontFamily: 'var(--font-body)', fontWeight: 400, opacity: 0.9 }}>
                        {p.badge}
                      </Badge>
                    )}
                  </div>
                  <div className="p-5">
                    <p className="text-xs tracking-wide uppercase mb-1.5" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)', fontWeight: 400 }}>
                      {p.subtitle}
                    </p>
                    <h3 className="text-lg tracking-tight mb-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)', fontWeight: 600 }}>
                      {p.title}
                    </h3>
                    <p className="text-xs line-clamp-2 mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', fontWeight: 300 }}>
                      {p.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)' }}>
                        {p.price.toFixed(2)} EUR
                      </span>
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/product/${p.campaign_id}`); }}
                        data-testid={`details-btn-${p.campaign_id}`}
                        className="inline-flex items-center gap-1 text-xs font-medium transition-colors hover:opacity-70"
                        style={{ fontFamily: 'var(--font-body)', color: 'var(--accent)' }}>
                        Scopri i dettagli <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trust / Conversion Bar */}
      <section className="border-t border-b py-8" style={{ borderColor: 'var(--border-light)', backgroundColor: 'var(--bg-primary)' }} data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: Truck, label: 'Spedizione 24/48h', sub: 'In tutta Italia' },
              { icon: Shield, label: 'Qualita Garantita', sub: 'Edizioni premium' },
              { icon: BookOpen, label: 'Oltre 200 Titoli', sub: 'Catalogo completo' },
              { icon: Tag, label: 'Offerte Esclusive', sub: 'Solo sul microsite' },
            ].map(({ icon: Icon, label, sub }) => (
              <div key={label} className="trust-item flex flex-col items-center gap-2">
                <Icon className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                <p className="text-xs font-medium" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>{label}</p>
                <p className="text-xs" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)', fontWeight: 300 }}>{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10" style={{ backgroundColor: 'var(--bg-dark)' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 text-center">
          <BookOpen className="w-5 h-5 mx-auto mb-3" style={{ color: 'var(--accent-light)' }} />
          <p className="text-lg tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-on-dark)' }}>
            Quintessenza Edizioni
          </p>
          <p className="text-xs" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)' }}>
            Selezione editoriale esclusiva &middot; Qualita senza compromessi
          </p>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      {activeProduct && (
        <div className="mobile-sticky-cta md:hidden" data-testid="mobile-sticky-cta">
          <Button onClick={() => handleBuyNow(activeProduct)} className="w-full rounded-sm py-3 text-sm font-medium tracking-wide"
            style={{ backgroundColor: 'var(--accent)', color: '#fff', fontFamily: 'var(--font-body)', border: 'none' }}>
            <ShoppingBag className="w-4 h-4 mr-2" /> Acquista Ora &middot; {discountedPrice} EUR
          </Button>
        </div>
      )}

      {/* Checkout Dialog - Full Form */}
      <Dialog open={checkoutOpen} onOpenChange={handleCheckoutClose}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'var(--font-heading)' }}>Checkout</DialogTitle>
            <DialogDescription className="text-sm" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>
              {checkoutStep === 1 ? 'Riepilogo e codice promo' : 'Dati di fatturazione e pagamento'}
            </DialogDescription>
          </DialogHeader>
          {checkoutProduct && (
            <div className="space-y-4 pt-2">
              {/* Product Summary - always visible */}
              <div className="flex gap-3 p-3 rounded-sm" style={{ backgroundColor: 'var(--bg-warm)', border: '1px solid var(--border-light)' }}>
                <img src={checkoutProduct.image_url} alt={checkoutProduct.title} className="w-14 h-20 object-cover rounded-sm" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{checkoutProduct.title}</h3>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{checkoutProduct.subtitle}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm font-medium" style={{ color: 'var(--accent)' }}>{discountedPrice} EUR</span>
                    {checkoutProduct.compare_at_price && <span className="text-xs line-through" style={{ color: 'var(--text-muted)' }}>{checkoutProduct.compare_at_price.toFixed(2)}</span>}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {checkoutProduct.is_digital
                      ? <><Mail className="w-3 h-3" style={{ color: 'var(--accent)' }} /><span className="text-xs" style={{ color: 'var(--text-muted)' }}>Prodotto digitale</span></>
                      : <><Truck className="w-3 h-3" style={{ color: 'var(--accent)' }} /><span className="text-xs" style={{ color: 'var(--text-muted)' }}>Prodotto fisico - spedizione inclusa</span></>
                    }
                  </div>
                </div>
              </div>

              {checkoutStep === 1 && (
                <>
                  {/* Promo Code */}
                  <div>
                    <Label className="text-xs" style={{ color: 'var(--text-muted)' }}>Codice Promozionale</Label>
                    <div className="flex gap-2 mt-1">
                      <Input value={promoInput} onChange={(e) => setPromoInput(e.target.value)} placeholder="Inserisci codice"
                        className="text-sm" style={{ fontFamily: 'var(--font-mono)' }} data-testid="checkout-promo-input" />
                      <Button variant="outline" size="sm" onClick={() => { setPromoInput(checkoutProduct.promo_code); toast.info('Codice applicato!'); }}
                        data-testid="apply-promo-btn" className="text-xs whitespace-nowrap">Applica</Button>
                    </div>
                    {promoInput === checkoutProduct.promo_code && (
                      <p className="text-xs mt-1" style={{ color: 'var(--accent-success)' }}>
                        {checkoutProduct.promo_discount_type === 'fixed'
                          ? `Sconto di ${checkoutProduct.promo_discount_value} EUR applicato!`
                          : `Sconto ${checkoutProduct.promo_discount_value}% applicato!`}
                      </p>
                    )}
                  </div>
                  <Button onClick={() => setCheckoutStep(2)} data-testid="checkout-next-btn"
                    className="w-full rounded-sm cta-glow" style={{ backgroundColor: '#3B82F6', color: '#fff' }}>
                    Procedi al Pagamento <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}

              {checkoutStep === 2 && (
                <>
                  {/* Billing */}
                  <div>
                    <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: 'var(--text-primary)' }}>
                      <CreditCard className="w-3.5 h-3.5" /> Dati di Fatturazione
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2"><Input value={billingForm.name} onChange={e => setBillingForm(p => ({...p, name: e.target.value}))}
                        placeholder="Nome completo" className="text-sm" data-testid="billing-name" /></div>
                      <div className="col-span-2"><Input type="email" value={billingForm.email} onChange={e => setBillingForm(p => ({...p, email: e.target.value}))}
                        placeholder="Email" className="text-sm" data-testid="billing-email" /></div>
                      <div className="col-span-2">
                        <Input value={billingForm.tax_code} onChange={e => setBillingForm(p => ({...p, tax_code: e.target.value.toUpperCase()}))}
                          placeholder="Codice Fiscale *" maxLength={16} className="text-sm uppercase" data-testid="billing-tax-code" required />
                        <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>Obbligatorio - 16 caratteri persona fisica / 11 cifre P.IVA</p>
                      </div>
                      <div className="col-span-2"><Input value={billingForm.address} onChange={e => setBillingForm(p => ({...p, address: e.target.value}))}
                        placeholder="Indirizzo" className="text-sm" data-testid="billing-address" /></div>
                      <Input value={billingForm.city} onChange={e => setBillingForm(p => ({...p, city: e.target.value}))}
                        placeholder="Citta" className="text-sm" data-testid="billing-city" />
                      <Input value={billingForm.zip} onChange={e => setBillingForm(p => ({...p, zip: e.target.value}))}
                        placeholder="CAP" className="text-sm" data-testid="billing-zip" />
                    </div>
                  </div>

                  {/* Invoice fields - always optional */}
                  <div>
                    <p className="text-xs font-medium mb-1 flex items-center gap-1" style={{ color: 'var(--text-primary)' }}>
                      Fatturazione <span className="font-normal" style={{ color: 'var(--text-muted)' }}>(facoltativo)</span>
                    </p>
                    <p className="text-[11px] mb-2" style={{ color: 'var(--text-muted)' }}>
                      Compila se hai bisogno della fattura. Lascia vuoto per ricevuta.
                    </p>
                    <div className="grid grid-cols-1 gap-2 p-3 rounded-sm" style={{ backgroundColor: 'var(--bg-warm)', border: '1px solid var(--border-light)' }}>
                      <Input value={invoiceForm.company_name} onChange={e => setInvoiceForm(p => ({...p, company_name: e.target.value}))}
                        placeholder="Ragione Sociale (opz.)" className="text-sm" data-testid="invoice-company" />
                      <Input value={invoiceForm.vat_number} onChange={e => setInvoiceForm(p => ({...p, vat_number: e.target.value}))}
                        placeholder="Partita IVA (opz.)" className="text-sm" data-testid="invoice-vat" />
                      <Input value={invoiceForm.sdi_code} onChange={e => setInvoiceForm(p => ({...p, sdi_code: e.target.value}))}
                        placeholder="Codice Univoco / SDI (opz.)" className="text-sm" data-testid="invoice-sdi" />
                    </div>
                  </div>

                  {/* Shipping or Digital Delivery */}
                  {checkoutProduct.is_digital ? (
                    <div>
                      <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: 'var(--text-primary)' }}>
                        <Mail className="w-3.5 h-3.5" /> Email di Consegna Digitale
                      </p>
                      <Input type="email" value={deliveryEmail} onChange={e => setDeliveryEmail(e.target.value)}
                        placeholder="Email per ricevere il prodotto" className="text-sm" data-testid="delivery-email" />
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: 'var(--text-primary)' }}>
                        <MapPin className="w-3.5 h-3.5" /> Indirizzo di Spedizione
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="col-span-2"><Input value={shippingForm.name} onChange={e => setShippingForm(p => ({...p, name: e.target.value}))}
                          placeholder="Nome destinatario (se diverso)" className="text-sm" data-testid="shipping-name" /></div>
                        <div className="col-span-2"><Input value={shippingForm.address} onChange={e => setShippingForm(p => ({...p, address: e.target.value}))}
                          placeholder="Indirizzo di spedizione" className="text-sm" data-testid="shipping-address" /></div>
                        <Input value={shippingForm.city} onChange={e => setShippingForm(p => ({...p, city: e.target.value}))}
                          placeholder="Citta" className="text-sm" data-testid="shipping-city" />
                        <Input value={shippingForm.zip} onChange={e => setShippingForm(p => ({...p, zip: e.target.value}))}
                          placeholder="CAP" className="text-sm" data-testid="shipping-zip" />
                      </div>
                    </div>
                  )}

                  {/* Payment Method */}
                  <div>
                    <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Metodo di Pagamento</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => setPaymentMethod('stripe')} data-testid="pay-stripe-btn"
                        className={`p-3 rounded-sm border text-center transition-all ${paymentMethod === 'stripe' ? 'ring-2' : ''}`}
                        style={{ borderColor: paymentMethod === 'stripe' ? 'var(--accent)' : 'var(--border-light)', ringColor: 'var(--accent)' }}>
                        <CreditCard className="w-5 h-5 mx-auto mb-1" style={{ color: paymentMethod === 'stripe' ? 'var(--accent)' : 'var(--text-muted)' }} />
                        <span className="text-xs font-medium" style={{ color: paymentMethod === 'stripe' ? 'var(--accent)' : 'var(--text-secondary)' }}>Carta</span>
                      </button>
                      <button onClick={() => setPaymentMethod('paypal')} data-testid="pay-paypal-btn"
                        className={`p-3 rounded-sm border text-center transition-all ${paymentMethod === 'paypal' ? 'ring-2' : ''}`}
                        style={{ borderColor: paymentMethod === 'paypal' ? '#0070BA' : 'var(--border-light)', ringColor: '#0070BA' }}>
                        <span className="text-lg font-bold block mb-0.5" style={{ color: paymentMethod === 'paypal' ? '#0070BA' : 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>PP</span>
                        <span className="text-xs font-medium" style={{ color: paymentMethod === 'paypal' ? '#0070BA' : 'var(--text-secondary)' }}>PayPal</span>
                      </button>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setCheckoutStep(1)} className="flex-1 rounded-sm text-sm">Indietro</Button>
                    <Button onClick={handleCheckout} disabled={checkoutLoading} data-testid="confirm-checkout-btn"
                      className="flex-1 rounded-sm cta-glow text-sm font-medium"
                      style={{ backgroundColor: paymentMethod === 'paypal' ? '#0070BA' : '#3B82F6', color: '#fff', textShadow: '0 0 8px rgba(255,255,255,0.2)' }}>
                      {checkoutLoading ? 'Elaborazione...' : paymentMethod === 'paypal' ? 'Paga con PayPal' : 'Paga con Carta'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
